import React, { useCallback, useContext, useEffect, useMemo } from "react";
import "./case-content.component.scoped.scss";
import CaseHeaderComponent from "../case-header/case-header.component";
import { Box } from "@material-ui/core";
import CaseTabsComponent from "../tabs/case-tabs.component";
import ScenarioTabContentComponent from "../scenario-tab-content/scenario-tab-content.component";
import { CaseTabContent } from "../../../types/case-tabs";
import ApplicationAreasTabContentComponent from "../application-areas-tab-content/application-areas-tab-content.component";
import ClassificationTabContentComponent from "../classification-tab-content/classification-tab-content.component";
import DiscussionTabContentComponent from "../discussion-tab-content/discussion-tab-content.component";
import TeamTabContentComponent from "../team-tab-content/team-tab-content.component";
import TodoTabContentComponent from "../todoList-tab-content/todoList-tab-content.component";
import { CasesService } from "../../../../../services/cases/cases.service";
import { MapperService } from "../../../../../services/mapper/mapper.service";
import { CommonService } from "../../../../../services/common/common.service";
import {
  CaseContext,
  GlobalContext,
  UsersProvider,
} from "../../../../../store";
import { useTranslation } from "react-i18next";
import { dummyQuestions } from "../../../../../statics/dummy-questions";
import { history } from "react-router-guard";
import { extractIds, showToastMsg } from "../../../../../helpers";
import { LookupsService } from "../../../../../services/lookups/lookups.service";
import CaseActionsComponent from "../case-actions/case-actions.component";
import PrintCaseContentComponent from "../../print/print-case-content/print-case-content.component";
import { Case, CaseDetailsForm } from "../../../../../types/case";
import { dummyFiles } from "../../../../../statics/dummy-files";
import { ContentLayout } from "../../../../../layouts/content/content.layout";
import { StagesService } from "../../../../../services/stages.services";
import { PLACEHOLDER_LENGTHS } from "../../../types/placeholder-data";
import { BreadcrumbComponent } from "../../../../../components/breadcrumb/breadcrumb.component";

const ACTIVE_TABS = {
  SCENARIO: 1,
  APPLICATION_AREAS: 2,
  CLASSIFICATION: 3,
  DISCUSSION: 4,
  TEAM: 5,
  TODO: 6,
} as const;

function buildCaseDetailsForm(
  mappedCase: Case | undefined,
  caseDetails: Case | undefined,
): CaseDetailsForm {
  const algorithms = mappedCase?.algorithms ?? caseDetails?.algorithms ?? [];
  const operations = mappedCase?.operations ?? caseDetails?.operations ?? [];
  const areas = mappedCase?.areas ?? caseDetails?.areas ?? [];
  const techniques = mappedCase?.techniques ?? caseDetails?.techniques ?? [];
  const machineLearning =
    mappedCase?.machineLearning ?? caseDetails?.machineLearning ?? [];

  return {
    version: String(mappedCase?.version ?? caseDetails?.version ?? ""),
    title: mappedCase?.title ?? caseDetails?.title ?? "",
    brief: mappedCase?.brief ?? caseDetails?.brief ?? "",
    description: mappedCase?.description ?? caseDetails?.description ?? "",
    scenarioDescription:
      mappedCase?.scenarioDescription ?? caseDetails?.scenarioDescription ?? "",
    link: mappedCase?.link ?? caseDetails?.link ?? "",
    algorithmsIds: extractIds(algorithms),
    operationsIds: extractIds(operations),
    areasIds: extractIds(areas),
    techniquesIds: extractIds(techniques),
    machineLearningIds: extractIds(machineLearning),
    algorithms,
    operations,
    areas,
    techniques,
    machineLearning,
  };
}

export const CaseContentComponent = ({ caseId }: { caseId: number }) => {
  const { t } = useTranslation();
  const { setPageTitle } = useContext(GlobalContext);
  const casesService = useMemo(() => new CasesService(), []);
  const lookupsService = useMemo(() => new LookupsService(), []);
  const mapperService = useMemo(() => new MapperService(), []);
  const stagesService = useMemo(() => new StagesService(), []);
  const commonService = useMemo(() => new CommonService(), []);

  const {
    // case
    setCaseDetails,
    setCaseDetailsForm,
    caseDetails,
    // Lookups
    setCaseUsers,
    setLands,
    setBranches,
    setAreas,
    setOperations,
    setAlgorithms,
    setTechniques,
    setMachineLearningTypes,
    // Tabs
    setActiveTab,
    activeTab,
    // Case Info
    setCaseStatuses,
    setCaseQuestions,
    setCaseFiles,
    // Loading
    setLoading,
    setClassificationLoading,
    setWorkflowLoading,
    setUsersLoading,
    setFilesLoading,
    // Ignore Update Case
    setIgnoreUpdateCase,
    ignoreUpdateCase,
    setCaseTotalQuestions,
    setCaseTotalAnsweredQuestions,
    setSelectedFile,
  } = useContext(CaseContext);

  ///////////////////////Start Fetch Case Details/////////////////////////////////////
  const fetchCaseDetails = async (id: number) => {
    try {
      setLoading(true);
      // Fetch case details from API
      const caseDetailsData = await casesService.details(id);
      // Handle case not found (API returns string "Not found" or error)
      if (
        !caseDetailsData ||
        (typeof caseDetailsData === "string" && caseDetailsData === "Not found")
      ) {
        history.push("/dashboard");
        showToastMsg("error", t("case not found"));
        setLoading(false);
        return;
      }
      // Set page title and meta information
      if (caseDetailsData.internalId) {
        commonService.setTitle(caseDetailsData.internalId);
        setPageTitle(
          `${t("PAGE_META_TITLES.USE_CASE_MANAGER")} - ${
            caseDetailsData.internalId
          }`,
        );
      }

      // Fetch and map stages
      const stages = await stagesService.list();
      const mappedStatuses = mapperService.mapStatuses(stages);
      // Map case data with stages
      const { mappedCase, mappedtimelineStatuses } = mapperService.mapCase(
        caseDetailsData,
        mappedStatuses,
      );

      // Update state with mapped case data
      setCaseDetails(mappedCase);
      resetChanges(mappedCase);
      setCaseStatuses(mappedtimelineStatuses);
    } catch (error) {
      console.error("Error fetching case details:", error);
      showToastMsg("error", t("error loading case details"));
      history.push("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const resetChanges = useCallback(
    (mappedCase?: Case) => {
      const caseDetailsForm = buildCaseDetailsForm(mappedCase, caseDetails);
      setCaseDetailsForm(caseDetailsForm);
      setIgnoreUpdateCase(ignoreUpdateCase + 1);
    },
    [caseDetails, setCaseDetailsForm, setIgnoreUpdateCase, ignoreUpdateCase],
  );

  useEffect(() => {
    fetchCaseDetails(caseId);
    getUsers(caseId);
    // eslint-disable-next-line
  }, [caseId]);
  //******************* End Fetch Case Details ********************************/

  ///////////////////////Start Fetch Case Questions/////////////////////////////////////
  const fetchCaseQuestions = useCallback(
    async (id: number) => {
      setWorkflowLoading(true);
      setCaseQuestions(dummyQuestions);
      try {
        const caseQuestions = await casesService.caseQuestions(id);
        const {
          totalQuestions,
          totalAnsweredQuestions,
          formattedCaseQuestions,
        } = mapperService.mapCaseQuestions(caseQuestions);
        setCaseTotalQuestions(totalQuestions);
        setCaseTotalAnsweredQuestions(totalAnsweredQuestions);
        setCaseQuestions(formattedCaseQuestions);
      } finally {
        setTimeout(() => setWorkflowLoading(false), 100);
      }
    },
    [
      casesService,
      mapperService,
      setWorkflowLoading,
      setCaseQuestions,
      setCaseTotalQuestions,
      setCaseTotalAnsweredQuestions,
    ],
  );
  //******************* End Fetch Case Questions ********************************/

  ///////////////////////Start Fetch Case Files/////////////////////////////////////
  const fetchCaseFiles = async (id: number) => {
    setFilesLoading(true);
    setCaseFiles(dummyFiles);
    const caseFiles = await casesService.caseFiles(id);
    if (caseFiles.length) setSelectedFile(caseFiles[0]);
    setTimeout(() => {
      setCaseFiles(caseFiles);
      setFilesLoading(false);
    }, 100);
  };
  useEffect(() => {
    fetchCaseFiles(caseId);
    // eslint-disable-next-line
  }, [caseId]);
  //******************* End Fetch Case Files ********************************/

  ///////////////////////START LOOKUPS DATA/////////////////////////////////
  const getUsers = useCallback(
    async (id: number, params?: any) => {
      setCaseUsers([]);
      setUsersLoading(true);
      const usersResult = await casesService.caseUsers(id, params);
      setCaseUsers(usersResult);
      setUsersLoading(false);
    },
    [casesService, setCaseUsers, setUsersLoading],
  );

  const getLands = useCallback(async () => {
    const lands = await lookupsService.listLands();
    setLands(lands);
  }, [lookupsService, setLands]);

  const getBranches = useCallback(async () => {
    const branches = await lookupsService.listBranches();
    setBranches(branches);
  }, [lookupsService, setBranches]);

  // Scenario Tab Data
  const getAreas = useCallback(async () => {
    setAreas(Array.from({ length: PLACEHOLDER_LENGTHS.AREAS }));
    const areas = await lookupsService.listAIAreas();
    setAreas(areas);
  }, [lookupsService, setAreas]);

  const getOperations = useCallback(async () => {
    setOperations(Array.from({ length: PLACEHOLDER_LENGTHS.OPERATIONS }));
    const operations = await lookupsService.listOperation();
    setOperations(operations);
  }, [lookupsService, setOperations]);

  // Classification Tab Data
  const getAlgorithms = useCallback(async () => {
    setAlgorithms(Array.from({ length: PLACEHOLDER_LENGTHS.ALGORITHMS }));
    const algorithms = await lookupsService.listAIAlgorithms();
    setAlgorithms(algorithms);
    console.log(algorithms);
  }, [lookupsService, setAlgorithms]);

  const getTechniques = useCallback(async () => {
    setTechniques(Array.from({ length: PLACEHOLDER_LENGTHS.TECHNIQUES }));
    const techniques = await lookupsService.listAITechniques();
    setTechniques(techniques);
  }, [lookupsService, setTechniques]);

  const getMachineLearning = useCallback(async () => {
    setMachineLearningTypes(
      Array.from({ length: PLACEHOLDER_LENGTHS.MACHINE_LEARNING }),
    );
    const machineLearningTypes = await lookupsService.listMachineLearningTypes();
    setMachineLearningTypes(machineLearningTypes);
  }, [lookupsService, setMachineLearningTypes]);

  useEffect(() => {
    getLands();
    getAreas();
    getBranches();
    getOperations();
    // eslint-disable-next-line
  }, []);
  ///////////////////////END LOOKUPS DATA/////////////////////////////////

  ///////////////////////START FETCH TABS/////////////////////////////////
  const fetchClassificationTab = useCallback(async () => {
    setClassificationLoading(true);
    try {
      await Promise.all([
        getAlgorithms(),
        getTechniques(),
        getMachineLearning(),
      ]);
    } finally {
      setClassificationLoading(false);
    }
  }, [
    setClassificationLoading,
    getAlgorithms,
    getTechniques,
    getMachineLearning,
  ]);

  const fetchDiscussionTab = useCallback(() => {
    fetchCaseQuestions(caseId);
  }, [caseId, fetchCaseQuestions]);

  const fetchTeamTab = useCallback(() => {
    getUsers(caseId);
  }, [caseId, getUsers]);

  useEffect(() => {
    resetChanges();
    const tabHandlers: Partial<Record<number, () => void | Promise<void>>> = {
      [ACTIVE_TABS.CLASSIFICATION]: fetchClassificationTab,
      [ACTIVE_TABS.DISCUSSION]: fetchDiscussionTab,
      [ACTIVE_TABS.TEAM]: fetchTeamTab,
    };
    const handler = tabHandlers[activeTab];
    if (handler) handler();
    // eslint-disable-next-line
  }, [activeTab, caseId]);
  ///////////////////////END FETCH TABS/////////////////////////////////

  const caseTabContent: CaseTabContent[] = useMemo(
    () => [
      {
        tab: ACTIVE_TABS.SCENARIO,
        component: <ScenarioTabContentComponent />,
      },
      {
        tab: ACTIVE_TABS.APPLICATION_AREAS,
        component: <ApplicationAreasTabContentComponent />,
      },
      {
        tab: ACTIVE_TABS.CLASSIFICATION,
        component: <ClassificationTabContentComponent />,
      },
      {
        tab: ACTIVE_TABS.DISCUSSION,
        component: <DiscussionTabContentComponent />,
      },
      {
        tab: ACTIVE_TABS.TEAM,
        component: (
          <UsersProvider>
            <TeamTabContentComponent sortUsers={getUsers} />
          </UsersProvider>
        ),
      },
      {
        tab: ACTIVE_TABS.TODO,
        component: <TodoTabContentComponent />,
      },
    ],
    [getUsers],
  );
  const tabContent = caseTabContent[activeTab - 1]?.component ?? null;

  return (
    <>
      <Box className="content mt-4" style={{ position: "relative" }}>
        <Box className="mb-5" style={{ minHeight: "100vh" }}>
          <ContentLayout>
            {caseDetails && caseDetails.title && (
              <BreadcrumbComponent
                items={[
                  {
                    label: t("AI-Use Cases"),
                    path: "/ai-use-cases",
                    separator: "-",
                  },
                  { label: caseDetails.title },
                ]}
              />
            )}
            <Box className="header-content mb-4">
              <CaseHeaderComponent />
            </Box>
            <Box className="case__content mb-0">
              <Box className="info">
                <Box className="tabs">
                  <CaseTabsComponent
                    onChangeTab={(tab: number) => setActiveTab(tab)}
                  />
                </Box>
                <Box className="py-3">{tabContent}</Box>
              </Box>
            </Box>
          </ContentLayout>
        </Box>
        <Box className="case__actions">
          <ContentLayout>
            <CaseActionsComponent onResetChanges={resetChanges} />
          </ContentLayout>
        </Box>
      </Box>
      <Box className="print" id="print-usecase">
        <PrintCaseContentComponent />
      </Box>
    </>
  );
};
